/**
 * Het niveau van een held (SPEC §3).
 *
 * **Afgeleid, nooit opgeslagen.** Een kolom `niveau` zou uit de pas lopen met
 * de betalingen zodra er één handmatig gecorrigeerd wordt; één functie kan
 * dat niet. Het scherm en de Worker rekenen allebei hiermee, zodat de muur en
 * het beheer nooit twee verschillende niveaus laten zien.
 *
 * Twee bronnen, en de hoogste wint:
 * - het aantal **betaalde jaarbijdragen** — na drie volle jaren (dus bij de
 *   vierde bijdrage) zilver, na vijf (de zesde) goud; de drempels komen uit
 *   `instellingen.niveaus`;
 * - een **afgekocht** niveau.
 *
 * Afkoop telt niet als jaren: wie goud afkoopt en daarna stopt met betalen is
 * een opgezegde held, geen gouden.
 */

export type Niveau = 'brons' | 'zilver' | 'goud';

export const NIVEAUS: readonly Niveau[] = ['brons', 'zilver', 'goud'];

export const NIVEAUNAMEN: Record<Niveau, string> = {
  brons: 'Brons',
  zilver: 'Zilver',
  goud: 'Goud',
};

/** Het bijvoeglijk naamwoord: "gouden held", nooit "goud held". */
export const NIVEAU_BIJVOEGLIJK: Record<Niveau, string> = {
  brons: 'bronzen',
  zilver: 'zilveren',
  goud: 'gouden',
};

export interface Drempels {
  zilverNaJaren: number;
  goudNaJaren: number;
}

/** Besluit van 6 oktober 2026: drie volle jaren brons, dan zilver; vijf volle jaren, dan goud. */
export const STANDAARD_DREMPELS: Drempels = { zilverNaJaren: 4, goudNaJaren: 6 };

const RANG: Record<Niveau, number> = { brons: 0, zilver: 1, goud: 2 };

export function hoogste(a: Niveau, b: Niveau): Niveau {
  return RANG[a] >= RANG[b] ? a : b;
}

export function isHoger(a: Niveau, dan: Niveau): boolean {
  return RANG[a] > RANG[dan];
}

/** Het niveau dat alleen uit de betaalde jaren volgt. */
export function niveauUitJaren(betaaldeJaren: number, drempels: Drempels): Niveau {
  if (betaaldeJaren >= drempels.goudNaJaren) return 'goud';
  if (betaaldeJaren >= drempels.zilverNaJaren) return 'zilver';
  return 'brons';
}

/** Het niveau van een held: het hoogste van verdiend en afgekocht. */
export function niveauVan(
  betaaldeJaren: number,
  afgekocht: Niveau | null,
  drempels: Drempels = STANDAARD_DREMPELS,
): Niveau {
  const verdiend = niveauUitJaren(betaaldeJaren, drempels);
  return afgekocht ? hoogste(verdiend, afgekocht) : verdiend;
}

export interface Vooruitzicht {
  niveau: Niveau;
  /** Hoeveel betaalde jaren er nog bij moeten. */
  overJaren: number;
}

/**
 * Wat is het eerstvolgende niveau dat deze held nog kan **verdienen**, en
 * over hoeveel betaalde jaren? `null` als er niets meer te halen valt.
 */
export function volgendNiveau(
  betaaldeJaren: number,
  afgekocht: Niveau | null,
  drempels: Drempels = STANDAARD_DREMPELS,
): Vooruitzicht | null {
  const huidig = niveauVan(betaaldeJaren, afgekocht, drempels);
  if (huidig === 'goud') return null;
  if (huidig === 'zilver') {
    return { niveau: 'goud', overJaren: Math.max(1, drempels.goudNaJaren - betaaldeJaren) };
  }
  return { niveau: 'zilver', overJaren: Math.max(1, drempels.zilverNaJaren - betaaldeJaren) };
}

/** Welke afkoop heeft voor deze held nog zin? */
export function afkoopbaar(huidig: Niveau): Niveau[] {
  return NIVEAUS.filter((n) => isHoger(n, huidig));
}

export function alsNiveau(waarde: unknown): Niveau | null {
  return waarde === 'brons' || waarde === 'zilver' || waarde === 'goud' ? waarde : null;
}

/**
 * Een jubileum: elke vijf betaalde jaren (SPEC §6.5). Geeft het aantal jaren
 * terug als dit een jubileumjaar is, anders `null`. Op de muur het hele jaar
 * zichtbaar, in de mail bij de bijdrage die het jubileum maakt.
 */
export function jubileum(betaaldeJaren: number): number | null {
  return betaaldeJaren >= 5 && betaaldeJaren % 5 === 0 ? betaaldeJaren : null;
}
